import { Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges, forwardRef, Optional, ChangeDetectorRef } from '@angular/core';
import { FormGroup, FormBuilder, FormArray, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BuilderNode, SearchFieldDependency } from '../../interfaces/builder-screen.models';
import { WebhookField, WebhookConfiguration, Variable } from '../../interfaces/webhook-integration.models';
import { DataBindingService } from '../../../screen-engine/services/data-binding.service';
import { MasterDetailService } from '../../../screen-engine/services/master-detail.service';
import { DataSyncService } from '../../../screen-engine/services/data-sync.service';
import { BuilderScreenBetaService } from '../../services/builder-screen.service';
import { Subscription, Subject } from 'rxjs';
import { takeUntil, filter } from 'rxjs/operators';

@Component({
  selector: 'app-webhook-adapter-beta',
  templateUrl: './webhook-adapter.component.html',
  styleUrls: ['./webhook-adapter.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => WebhookAdapterBetaComponent),
      multi: true
    }
  ],
  standalone: false
})
export class WebhookAdapterBetaComponent implements OnInit, OnChanges, OnDestroy, ControlValueAccessor {
  @Input() node!: BuilderNode;
  @Input() isPreview = true; // Modo de visualização para canvas vs modo de formulário real

  // Inputs do componente webhook
  webhookField!: WebhookField;
  webhookConfig!: WebhookConfiguration;
  formGroup!: FormGroup;
  variables: Variable[] = [];
  
  // ✅ CORREÇÃO: Flag para garantir que o form está pronto antes de renderizar
  formReady = false;

  private subscription: Subscription = new Subscription();
  private dataSubscription: Subscription = new Subscription(); // Data Binding
  private masterDetailSubscription: Subscription = new Subscription(); // Master-Detail
  private destroy$ = new Subject<void>(); // Para limpar subscriptions
  private onChange = (value: any) => {};
  private onTouched = () => {};
  private isUpdatingFromBinding = false; // Flag para evitar loops no data binding
  private lastCurrentSelectionKey: string | null = null;
  private lastMasterDetailSelectionKey: string | null = null;

  // ── Dependências entre campos de pesquisa ──────────────────────────
  dependencyMet = true;                         // Todas as dependências estão satisfeitas?
  dependencyMessage = '';                        // Mensagem quando não satisfeita
  private dependencyValues = new Map<string, any>(); // sourceDataPath → valor atual
  private dependencyBaseCondition = '';           // Condition original do searchProperties
  private hasDependencies = false;               // Atalho p/ saber se tem deps

  constructor(
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    @Optional() private dataBindingService: DataBindingService, // Para data binding global
    @Optional() private masterDetailService: MasterDetailService, // Para master-detail
    @Optional() private dataSyncService: DataSyncService, // Para sincronização de formulários
    @Optional() private builderService: BuilderScreenBetaService // Para contexto da grade (pai vs filha)
  ) {}

  ngOnInit(): void {
    this.initializeWebhookField();
    this.initializeFormGroup();
    
    // Marcar form como pronto APÓS inicialização completa
    this.formReady = true;
    this.cdr.detectChanges();
    
    // Subscrever aos dados para binding automático (injeção global)
    this.subscribeToDataBinding();
    
    // Subscrever ao Master-Detail (seleção de linha em grid)
    this.subscribeToMasterDetail();
    
    // 🆕 Subscrever ao currentSelection (para grades aninhadas)
    this.subscribeToCurrentSelection();

    // 🆕 Configurar dependências entre campos de pesquisa (cascading search)
    this.setupSearchDependencies();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // ✅ Detecta mudanças no node (propriedades ou updateKey) e atualiza validators
    if (changes['node'] && !changes['node'].firstChange) {
      const previousNode = changes['node'].previousValue as BuilderNode;
      const currentNode = changes['node'].currentValue as BuilderNode;
      
      // Verifica se o updateKey mudou (indica atualização de propriedades)
      if (previousNode?.updateKey !== currentNode?.updateKey) {
        // IMPORTANTE: Atualizar this.node antes de reinicializar
        this.node = currentNode;
        
        // ✅ CORREÇÃO: NÃO destruir e recriar o FormGroup (causa erro _rawValidators)
        // Em vez disso, apenas atualizar o webhookField e os validators do control existente
        this.initializeWebhookField();
        
        // Atualiza validators do control existente
        if (this.formGroup) {
          const control = this.formGroup.get(this.webhookField.field);
          if (control) {
            // Atualiza os validators sem destruir o control
            control.setValidators(this.getValidators());
            control.updateValueAndValidity({ emitEvent: false });
          }
        }
        
        // Força detecção para atualizar o template com as novas propriedades
        this.cdr.detectChanges();
      }
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Implementação do ControlValueAccessor
  writeValue(value: any): void {
    if (this.formGroup) {
      const safeValue = this.getSafeValueForControl(value);
      const control = this.formGroup.get(this.webhookField.field);
      
      // DIAGNÓSTICO TEMPORÁRIO: Log para rastrear quem está limpando valores
      const dataPath = (this.node?.props as any)?.dataPath;
      const currentValue = control?.value;
      if (control) {
        // 🆕 CRÍTICO: Marcar flag para evitar que valueChanges propague
        this.isUpdatingFromBinding = true;
        
        try {
          // Para componentes NG-ZORRO (dropdown, number, textarea, switch): forçar reset antes de setar o novo valor
          // Isso garante que o componente detecte a mudança e renderize corretamente
          if (this.node.type === 'dropdown' || this.node.type === 'select' || 
              this.node.type === 'number' || this.node.type === 'textarea' || 
              this.node.type === 'switch') {
            // Reset e re-set para forçar detecção de mudança
            control.setValue(null, { emitEvent: false });
            this.cdr.detectChanges();
            control.setValue(safeValue, { emitEvent: false });
            this.cdr.detectChanges();
          } else {
            control.setValue(safeValue, { emitEvent: false });
          }
        } finally {
          // Desativar flag após pequeno delay
          setTimeout(() => {
            this.isUpdatingFromBinding = false;       
          }, 50);
        }
      }
    }
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (this.formGroup) {
      if (isDisabled) {
        this.formGroup.disable();
      } else {
        this.formGroup.enable();
      }
    }
  }

  private initializeWebhookField(): void {
    // Converte BuilderNode para WebhookField
    // IMPORTANTE: Sempre criar novo objeto para forçar detecção de mudança
    this.webhookField = this.convertBuilderNodeToWebhookField(this.node);
    
    // Cria configuração mínima de webhook
    // Também criar novo array para garantir mudança de referência
    this.webhookConfig = {
      fields: [{ ...this.webhookField }],
      variables: this.variables
    };
  }

  private initializeFormGroup(): void {
    // Cria grupo de formulário para componente webhook
    const formControls: any = {};
    
    // ✅ PROTEÇÃO EXTRA: Sempre usar valor seguro, nunca undefined
    const defaultValue = this.getDefaultValue();
    const safeValue = defaultValue === undefined ? null : defaultValue;
    
    // Para componentes keyValue e keyValueObject, cria um FormArray
    if (this.node.type === 'keyValue' || this.node.type === 'keyValueObject') {
      formControls[this.webhookField.field] = this.fb.array([]);
    } else {
      formControls[this.webhookField.field] = [safeValue, this.getValidators()];
    }
    
    this.formGroup = this.fb.group(formControls);

    // Desabilitar o FormControl se isDisabled está marcado nas props
    // (nz-select com formControlName ignora [nzDisabled] e respeita apenas o estado do control)
    if (this.webhookField.isDisabled && !this.isPreview) {
      const ctrl = this.formGroup.get(this.webhookField.field);
      if (ctrl) {
        ctrl.disable({ emitEvent: false });
      }
    }

    // Subscreve a mudanças do formulário
    if (!this.isPreview) {
      this.subscription.add(
        this.formGroup.valueChanges.subscribe(value => {
          if (this.isUpdatingFromBinding) {
            return;
          }
          
          const newValue = value[this.webhookField.field];
          

          
          // Propagar mudança via ControlValueAccessor (para ngModel/formControlName)
          this.onChange(newValue);
          this.onTouched();
          
          // 🆕 Propagar mudança para DataSyncService (para sincronização com grade)
          if (this.dataSyncService && this.node?.props) {
            const dataPath = (this.node.props as any).dataPath;
            if (dataPath) {
              const formContextArrayPath = this.builderService && this.node?.id
                ? (() => {
                    const panelId = this.builderService.getContainingPanelId(this.node.id);
                    const formDisplayArrayPath = this.dataSyncService?.getCurrentSelection?.()?.arrayPath
                      ?? this.dataSyncService?.getMainSelection?.()?.arrayPath;
                    return panelId ? this.builderService.getGridDataPathForPanel(panelId, formDisplayArrayPath) : undefined;
                  })()
                : undefined;
              this.dataSyncService.onFormFieldChange(dataPath, newValue, formContextArrayPath);
              
              this.dataSyncService.emitFieldSync(dataPath, newValue, this.node.id);

              this.executeDirectFieldMappings(newValue);
            }
          }
        })
      );
    }
    
    // ✅ CORREÇÃO: Para componentes NG-ZORRO, forçar atualização visual inicial
    // Esses componentes precisam de um "empurrão" para renderizar o valor inicial corretamente
    const isNgZorroComponent = ['dropdown', 'select', 'number', 'textarea', 'switch'].includes(this.node.type);
    
    if (isNgZorroComponent) {
      // Para switch, precisa forçar mesmo se o valor for false (pois false é um valor válido)
      const shouldForceUpdate = this.node.type === 'switch' || 
                                (safeValue !== null && safeValue !== undefined && safeValue !== '');
      
      if (shouldForceUpdate) {
        setTimeout(() => {
          const control = this.formGroup.get(this.webhookField.field);
          if (control) {
            const currentValue = control.value;
            // Reset e re-set para forçar renderização
            control.setValue(null, { emitEvent: false });
            this.cdr.detectChanges();
            control.setValue(currentValue, { emitEvent: false });
            this.cdr.detectChanges();
          }
        }, 100);
      }
    }
  }

  private convertBuilderNodeToWebhookField(node: BuilderNode): WebhookField {
    const props = node.props as any;
    
    const baseField: WebhookField = {
      field: `field_${node.id}`,
      type: this.mapBuilderTypeToWebhookType(node.type),
      label: props.label || '',
      placeholder: props.placeholder || '',
      required: props.required || false,
      isDisabled: props.isDisabled || false,
      colSpan: props.colSpan || 24
    };

    // Mapeia propriedades específicas baseadas no tipo de componente
    switch (node.type) {
      case 'date':
        return {
          ...baseField,
          dateFormat: props.dateFormat || 'DD/MM/YYYY'
        };
      
      case 'select':
        return {
          ...baseField,
          options: props.options || [],
          // Converte formato de opções se necessário
        };
      
      case 'dropdown':
        return {
          ...baseField,
          options: props.options || [],
          multiple: props.multiple || false,
          showSearch: props.showSearch || false
        };
      
      case 'querySelect':
        return {
          ...baseField,
          condition: props.condition || '',
          showKey: props.showKey || false,
          searchProperties: props.entityName ? {
            entityName: props.entityName,
            fields: {
              keyField: props.keyField || 'id',
              valueField: props.valueField || 'name'
            }
          } : undefined
        };
      
      case 'keyValue':
        return {
          ...baseField,
          autocompleteKey: props.autocompleteKey || [],
          autocompleteValue: props.autocompleteValue || []
        };
      
      case 'search':
        // Garantir que searchProperties sempre tenha valores válidos
        const searchProps = props.searchProperties || {};
        const hasValidEntityName = searchProps.entityName && searchProps.entityName.trim() !== '';
        const hasValidKeyField = searchProps.fields?.keyField && searchProps.fields.keyField.trim() !== '';
        const hasValidValueField = searchProps.fields?.valueField && searchProps.fields.valueField.trim() !== '';
        
        // Criar SEMPRE um novo objeto para forçar detecção de mudança
        const finalSearchProperties = {
          entityName: hasValidEntityName ? searchProps.entityName : (props.entityName || 'Mktpproducts'),
          fields: {
            keyField: hasValidKeyField ? searchProps.fields!.keyField : (props.keyField || 'CODEPRODUCT'),
            valueField: hasValidValueField ? searchProps.fields!.valueField : (props.valueField || 'NAME')
          }
        };
        
        const searchField: any = {
          ...baseField,
          searchProperties: finalSearchProperties,
          showKey: props.showKey || false
        };
        // Incluir fieldMappings para campos derivados (preenchimento automático ao selecionar)
        if (props.fieldMappings?.length > 0) {
          searchField.fieldMappings = props.fieldMappings;
        }
        return searchField;
      
      case 'divider':
        return {
          ...baseField,
          divider: {
            dashed: props.dashed || false,
            type: props.type || 'horizontal',
            orientation: props.orientation || 'center',
            color: props.color || '#d9d9d9'
          }
        };
      
      case 'section':
        return {
          ...baseField,
          sectionConfiguration: {
            title: props.title || '',
            collapsible: props.collapsible || false,
            collapsed: props.collapsed || false
          }
        };
      
      case 'number':
        // Propriedades de formatação do Number (NG-ZORRO)
        return {
          ...baseField,
          min: props.min,
          max: props.max,
          step: props.step,
          precision: props.precision,
          // Propriedades de formatação
          decimalPlaces: props.decimalPlaces ?? 2,
          formatThousands: props.formatThousands ?? false,
          thousandSeparator: props.thousandSeparator || '.',
          decimalSeparator: props.decimalSeparator || ',',
          addonBefore: props.addonBefore || '',
          addonAfter: props.addonAfter || ''
        };
      
      default:
        return baseField;
    }
  }

  private mapBuilderTypeToWebhookType(builderType: string): string {
    // Mapeamento direto para a maioria dos tipos
    const typeMap: Record<string, string> = {
      'keyValue': 'keyValue',
      'keyValueObject': 'keyValueObject',
      'querySelect': 'querySelect',
      'dropdown': 'select'  // NOVO: Dropdown usa o mesmo componente webhook-form-select
    };

    return typeMap[builderType] || builderType;
  }

  private getDefaultValue(): any {
    const props = this.node.props as any;
    
    switch (this.node.type) {
      case 'switch':
        // ✅ CORREÇÃO: Para switch, false é um valor válido - não usar ||
        return props.defaultValue !== undefined ? props.defaultValue : false;
      case 'number':
        // ✅ CORREÇÃO: Nunca retornar undefined para campos numéricos
        // Verificar defaultValue primeiro, depois min, depois null
        if (props.defaultValue !== undefined && props.defaultValue !== null) {
          return props.defaultValue;
        }
        return props.min !== undefined ? props.min : null;
      case 'select':
      case 'dropdown':
        // ✅ CORREÇÃO: Verificar defaultValue das props (vem da opção marcada como isDefault)
        if (props.defaultValue !== undefined && props.defaultValue !== null) {
          return props.multiple ? [props.defaultValue] : props.defaultValue;
        }
        // ✅ FALLBACK: Se não tem defaultValue, verificar options com isDefault
        if (props.options && Array.isArray(props.options)) {
          const defaultOption = props.options.find((opt: any) => opt.isDefault);
          if (defaultOption) {
            return props.multiple ? [defaultOption.value] : defaultOption.value;
          }
        }
        return props.multiple ? [] : null;
      case 'keyValue':
      case 'keyValueObject':
        return {};
      case 'tag':
        return [];
      case 'text':
      case 'textarea':
      case 'date':
        // ✅ NOVO: Verificar defaultValue para campos de texto e data
        if (props.defaultValue !== undefined && props.defaultValue !== null && props.defaultValue !== '') {
          return props.defaultValue;
        }
        return '';
      default:
        // Para outros tipos, verificar defaultValue nas props
        if (props.defaultValue !== undefined && props.defaultValue !== null) {
          return props.defaultValue;
        }
        return '';
    }
  }
  
  /**
   * Converte valores vazios para defaultValue e undefined para null
   * Evita erro "Cannot read properties of undefined" em componentes
   */
  private getSafeValueForControl(value: any): any {
    const props = this.node.props as any;
    
    // Para dropdowns/selects, converter o valor para o mesmo tipo das options
    // Isso evita problemas de comparação (número vs string)
    if ((this.node.type === 'dropdown' || this.node.type === 'select') && 
        props?.options && Array.isArray(props.options) && props.options.length > 0) {
      
      let useDefault = false;
      
      // Se tem valor, tentar fazer match com as options
      if (value !== undefined && value !== null && value !== '') {
        // Converter para string para comparação
        const valueStr = String(value);
        const matchingOption = props.options.find((opt: any) => String(opt.value) === valueStr);
        
        if (matchingOption) {
          // Retorna o valor da option (garante mesmo tipo)
          return matchingOption.value;
        } else {
          // ✅ CORREÇÃO: Não encontrou match, usar defaultValue
          useDefault = true;
        }
      } else {
        // Valor vazio
        useDefault = true;
      }
      
      // Se deve usar default (valor vazio OU valor não existe nas options)
      if (useDefault) {
        let defaultValue = props?.defaultValue;
        
        if (defaultValue === undefined || defaultValue === null) {
          const defaultOption = props.options.find((opt: any) => opt.isDefault);
          if (defaultOption) {
            defaultValue = defaultOption.value;
          }
        }
        
        if (defaultValue !== undefined && defaultValue !== null && defaultValue !== '') {
          return defaultValue;
        }
      }
      
      return props.multiple ? [] : null;
    }
    
    // ✅ CORREÇÃO: Para switches, false é um valor válido - tratar booleanos especialmente
    if (this.node.type === 'switch') {
      // Se o valor é booleano (true ou false), retornar como está
      if (typeof value === 'boolean') {
        return value;
      }
      // Se o valor for undefined/null, usar defaultValue
      if (value === undefined || value === null) {
        const defaultValue = props?.defaultValue;
        return defaultValue !== undefined ? defaultValue : false;
      }
      // Se o valor for string, converter para booleano
      if (typeof value === 'string') {
        return value === 'true' || value === '1';
      }
      // Para outros casos, converter para booleano
      return !!value;
    }
    
    // Se valor for vazio, verificar se existe defaultValue (para tipos não-booleanos)
    if (value === undefined || value === null || value === '') {
      const defaultValue = props?.defaultValue;
      
      if (defaultValue !== undefined && defaultValue !== null && defaultValue !== '') {
        return defaultValue;
      }
    }

    // Se for undefined ou null em campo numérico, retornar null (nz-input-number aceita null, mas não undefined)
    if ((value === undefined || value === null) && this.node.type === 'number') {
      return null;
    }
    
    // Para outros tipos, retornar o valor como está (ou null se undefined)
    return value === undefined ? null : value;
  }

  private getValidators(): any[] {
    const validators = [];
    const props = this.node.props as any;

    if (props.required) {
      // Adiciona validador obrigatório - seria necessário importar Validators
      // validators.push(Validators.required);
    }

    return validators;
  }

  // Manipulador de evento para mudanças do componente webhook
  onWebhookChange(event: any): void {
    if (!this.isPreview) {
      this.onChange(event);
      this.onTouched();
    }
  }

  /**
   * Subscreve aos dados injetados para preencher o campo automaticamente
   */
  private subscribeToDataBinding(): void {
    if (this.isPreview || !this.dataBindingService) {
      return;
    }
    
    
    this.dataSubscription = this.dataBindingService.getData$().subscribe(data => {
      if (!data) return;
      
      const dataPath = (this.node.props as any)?.dataPath;
      if (!dataPath) return;
      
      // CRÍTICO: NÃO atualizar se mudança veio do próprio formulário
      if (this.dataSyncService && this.dataSyncService.isUpdatingFromForm) {        
        return;
      }
      
      // CORREÇÃO: NÃO atualizar durante edição de célula na grade.
      // O processCellEdit atualiza o store (getData$ emite), mas resolvePath com dataPath
      // curto (ex: "title") resolve da RAIZ do JSON global, não da linha selecionada.
      // Resultado: undefined → null → campo limpo. O campo editado é re-preenchido via
      // emitFieldSync, mas todos os outros campos ficam limpos.
      if (this.dataSyncService && this.dataSyncService.isCellEditing) {
        return;
      }
      
      const value = this.dataBindingService.resolvePath(data, dataPath);
      
      const control = this.formGroup.get(this.webhookField.field);
      if (control) {
        const safeValue = this.getSafeValueForControl(value);
        const currentControlValue = control.value;
        
        // CORREÇÃO CRÍTICA: NÃO sobrescrever campos que já têm valor com null/undefined/vazio.
        // getData$ resolve dataPath da RAIZ do JSON global. Para campos de formulário com
        // dataPath curto (ex: "title", "brand"), a resolução falha (retorna null) porque o
        // campo está em response.magalu[0].title, não na raiz.
        // Sem esta proteção, TODOS os campos do formulário são limpos quando getData$ emite
        // (ex: após edição de célula, após injeção de dados, etc.)
        // Os campos são preenchidos corretamente por selectedRow$ e fieldSync$.
        const isValueEmpty = safeValue === null || safeValue === undefined || safeValue === '';
        const hasCurrentValue = currentControlValue !== null && 
                                currentControlValue !== undefined && 
                                currentControlValue !== '';
        
        if (isValueEmpty && hasCurrentValue) {
          return;
        }
        
        this.isUpdatingFromBinding = true;
        
        // Para dropdowns/selects: forçar reset antes de setar o novo valor
        if (this.node.type === 'dropdown' || this.node.type === 'select') {
          control.setValue(null, { emitEvent: false });
          this.cdr.detectChanges();
          control.setValue(safeValue, { emitEvent: true });
        } else {
          control.setValue(safeValue, { emitEvent: false });
        }
        
        this.isUpdatingFromBinding = false;
        
        // Forçar detecção de mudança para atualizar o componente visual
        this.cdr.detectChanges();
      }
    });
    
    this.subscription.add(this.dataSubscription);
  }

  /**
   * 🆕 Subscreve ao currentSelection (para grades aninhadas)
   * Grades aninhadas atualizam currentSelectionSubject quando linhas são selecionadas/editadas
   */
  private subscribeToCurrentSelection(): void {
    if (this.isPreview || !this.dataSyncService) {
      return;
    }

  

    this.dataSyncService.currentSelectionSubject.pipe(
      takeUntil(this.destroy$)
    ).subscribe(selection => {
      if (!selection || selection.index === -1) {
        return;
      }

      const selectionKey = `${selection.arrayPath || ''}::${selection.index}`;
      
      const dataPath = (this.node.props as any)?.dataPath;
      if (!dataPath) return;
      
      // CORREÇÃO: Se targetNodeIds fornecido, verificar se ESTE adapter está no set.
      if (selection.targetNodeIds && !selection.targetNodeIds.has(this.node?.id)) {
        return; // Este campo NÃO pertence ao painel da grade que emitiu
      }
      
      // CRÍTICO: Esta subscription é APENAS para grades FILHAS
      const mainSelection = this.dataSyncService.mainSelectionSubject.value;
      
      if (!selection.arrayPath) {
        return;
      }
      
      const isNestedGridSelection = selection.arrayPath !== mainSelection.arrayPath && 
                                    selection.arrayPath.startsWith(mainSelection.arrayPath + '.');
      
      if (!isNestedGridSelection) {
        return;
      }
      
      // Permitir atualização quando houver troca real de linha/contexto,
      // mesmo que a flag global ainda esteja ativa por alguns ms.
      if (this.dataSyncService && this.dataSyncService.isUpdatingFromForm &&
          this.lastCurrentSelectionKey === selectionKey) {
        return;
      }
      this.lastCurrentSelectionKey = selectionKey;
      
      const value = this.dataBindingService.resolvePath(selection.item, dataPath);
      
      const control = this.formGroup.get(this.webhookField.field);
      if (control) {
        const safeValue = this.getSafeValueForControl(value);
        
        // CORREÇÃO: Removidos os guards isValueEmpty/hasCurrentValue e control.dirty.
        // Em subscribeToCurrentSelection, a intenção é EXPLÍCITA: o usuário selecionou
        // outra linha na grade filha, então TODOS os campos devem atualizar — incluindo
        // limpar campos cujo novo valor é vazio e sobrescrever campos dirty.
        // (Esses guards permanecem em subscribeToData() onde são necessários.)
        this.isUpdatingFromBinding = true;
        control.setValue(safeValue, { emitEvent: false });
        control.markAsPristine(); // Resetar dirty para não bloquear trocas futuras
        this.cdr.detectChanges();
        setTimeout(() => {
          this.isUpdatingFromBinding = false;
        }, 50);
      }
    });
  }

  /**
   * Subscreve ao Master-Detail para preencher campos quando linha da grid é selecionada
   */
  private subscribeToMasterDetail(): void {
    if (this.isPreview || !this.masterDetailService) {
      return;
    }

    this.masterDetailSubscription = this.masterDetailService.selectedRow$.subscribe(state => {
      if (!state || !state.selectedRow) return;

      const selectionKey = `${state.arrayPath || ''}::${state.rowIndex ?? -1}::${state.sourceGridId || ''}`;
      
      const dataPath = (this.node.props as any)?.dataPath;
      if (!dataPath) return;
      
      // CORREÇÃO: Se targetPanelIds fornecido, verificar se ESTE adapter pertence a um dos painéis alvo.
      // Sem isso, selectedRow$ da grade principal contamina campos de abas filhas que têm
      // o mesmo dataPath (ex: "type" existe no painel principal E na aba Imagens).
      // A grade principal envia type="product", que sobrescreve "image/jpeg" na aba Imagens.
      if (state.targetPanelIds && state.targetPanelIds.length > 0 && this.builderService) {
        const myPanelId = this.builderService.getContainingPanelId(this.node?.id);
        if (myPanelId && !state.targetPanelIds.includes(myPanelId)) {
          return; // Este campo NÃO pertence aos painéis alvo da grade que emitiu selectedRow$
        }
      }
      
      // 🆕 CRÍTICO: NÃO atualizar se mudança veio do próprio formulário
      // Mesmo racional do currentSelection: bloquear apenas reprocessamento da mesma linha.
      if (this.dataSyncService && this.dataSyncService.isUpdatingFromForm &&
          this.lastMasterDetailSelectionKey === selectionKey) {
        
        return;
      }
      this.lastMasterDetailSelectionKey = selectionKey;
      
      const value = this.resolvePath(state.selectedRow, dataPath);
      const control = this.formGroup.get(this.webhookField.field);
      if (control) {
        const safeValue = this.getSafeValueForControl(value);
        
        // CORREÇÃO: Removida a guarda control.dirty que impedia a limpeza de campos
        // ao trocar de linha. Quando selectedRow$ emite, é porque uma nova linha foi
        // selecionada (ou adicionada), então TODOS os campos devem refletir os dados
        // da nova linha — incluindo limpar campos que na nova linha são null/undefined.
        // A guarda dirty causava: ao adicionar nova linha, campos de pesquisa (Empresa,
        // Contrato, Produto) retinham valores da linha anterior porque eram dirty.
        this.isUpdatingFromBinding = true;
        
        // Para dropdowns/selects: forçar reset antes de setar o novo valor
        // Isso garante que o Angular detecte a mudança mesmo se o valor for igual
        if (this.node.type === 'dropdown' || this.node.type === 'select') {
          // Primeiro reset para null (sem emitir evento)
          control.setValue(null, { emitEvent: false });
          this.cdr.detectChanges();
          
          // Depois setar o valor real (com emitir evento para forçar atualização)
          control.setValue(safeValue, { emitEvent: true });
        } else {
          control.setValue(safeValue, { emitEvent: false });
        }
        
        // Resetar dirty para não bloquear atualizações futuras
        control.markAsPristine();
        this.isUpdatingFromBinding = false;
        
        // Forçar detecção de mudança para atualizar o componente visual
        this.cdr.detectChanges();
      }
    });
    
    this.subscription.add(this.masterDetailSubscription);
    
    // CORREÇÃO: Inscrever-se em sincronização de campos duplicados
    this.subscribeToFieldSync();
  }

  /**
   * CORREÇÃO: Inscreve-se para receber atualizações de campos com mesmo dataPath
   * Isso permite que campos duplicados fiquem em sincronia
   */
  private subscribeToFieldSync(): void {
    if (this.isPreview || !this.dataSyncService) return;
    
    const myDataPath = (this.node?.props as any)?.dataPath;
    if (!myDataPath) return;
    
    const myNodeId = this.node?.id;
    
    this.subscription.add(
      this.dataSyncService.fieldSync$.pipe(
        filter(event => {
          // Filtrar por dataPath e excluir eventos do próprio campo
          if (event.dataPath !== myDataPath || event.sourceNodeId === myNodeId) return false;
          
          // CORREÇÃO: Se targetNodeIds está definido, APENAS aceitar se meu nodeId está no Set.
          if (event.targetNodeIds && !event.targetNodeIds.has(myNodeId!)) return false;
          
          return true;
        })
      ).subscribe(event => {
        
        const control = this.formGroup.get(this.webhookField.field);
        if (control) {
          const safeValue = this.getSafeValueForControl(event.value);
          
          // Marcar flag para evitar loop infinito
          this.isUpdatingFromBinding = true;
          control.setValue(safeValue, { emitEvent: false });
          this.isUpdatingFromBinding = false;
          
          // ✅ CORREÇÃO: Re-emitir fieldSync com o próprio nodeId quando o valor veio de um field mapping.
          // Como setValue usa { emitEvent: false }, o valueChanges não dispara, e dependências
          // downstream (cascading search) não são notificadas. Re-emitir resolve isso.
          if (event.sourceNodeId && event.sourceNodeId.startsWith('fieldMapping_')) {
            this.dataSyncService.emitFieldSync(myDataPath, safeValue, myNodeId!);
          }
          
          // Forçar atualização visual
          this.cdr.detectChanges();
        }
      })
    );
  }

  /**
   * Resolve um path JSON como 'nome' ou 'dados.nome' ou 'response.item_list[0].nome'
   */
  private resolvePath(data: any, path: string): any {
    if (!path || !data) return null;
    
    const keys = path.split('.');
    let value = data;
    
    for (const key of keys) {
      // Suporte para array index: 'items[0]'
      const arrayMatch = key.match(/^(.+)\[(\d+)\]$/);
      if (arrayMatch) {
        const arrayKey = arrayMatch[1];
        const arrayIndex = parseInt(arrayMatch[2], 10);
        value = value?.[arrayKey]?.[arrayIndex];
      } else {
        value = value?.[key];
      }
      
      if (value === null || value === undefined) {
        return null;
      }
    }
    
    return value;
  }

  /**
   * ⭐ NOVO: Retorna URL da imagem (com suporte a data binding e base64)
   * Aceita:
   * - URLs: http://exemplo.com/imagem.jpg
   * - Base64: data:image/png;base64,iVBORw0KGgoAAAANS...
   * - Data binding via dataPath
   */
  getImageUrl(): string {
    const props = this.node.props as any;
    
    // Prioridade: dataPath > imageUrl estática > placeholder
    if (props.dataPath && this.dataBindingService) {
      const data = this.dataBindingService.getCurrentData();
      if (data) {
        const resolvedUrl = this.resolvePath(data, props.dataPath);
        if (resolvedUrl) {
          return this.validateImageSource(resolvedUrl);
        }
      }
    }
    
    // Retorna URL estática (pode ser http/https ou data URI base64)
    const staticUrl = props.imageUrl || 'https://via.placeholder.com/300x200';
    return this.validateImageSource(staticUrl);
  }

  /**
   * Valida se a fonte da imagem é segura (URL ou Base64)
   */
  private validateImageSource(source: string): string {
    if (!source || typeof source !== 'string') {
      return 'https://via.placeholder.com/300x200?text=Sem+Imagem';
    }

    const trimmed = source.trim();

    // Aceita data URIs base64 válidas
    if (trimmed.startsWith('data:image/')) {
      // Valida formatos de imagem permitidos
      const validFormats = ['png', 'jpeg', 'jpg', 'gif', 'webp', 'svg+xml', 'bmp'];
      const formatMatch = trimmed.match(/^data:image\/([a-z+]+);base64,/i);
      
      if (formatMatch && validFormats.includes(formatMatch[1].toLowerCase().replace('+', '+'))) {
        return trimmed;
      } else {
        return 'https://via.placeholder.com/300x200?text=Base64+Invalido';
      }
    }

    // Aceita URLs válidas (http/https)
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
      try {
        const url = new URL(trimmed);
        
        // Verifica se parece ser uma página HTML do Unsplash (não uma imagem)
        if (url.hostname.includes('unsplash.com') && url.pathname.includes('/fotografias/')) {
          return 'https://via.placeholder.com/300x200?text=Link+do+Unsplash+Invalido';
        }
        
        // Verifica extensão de arquivo de imagem comum
        const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp'];
        const hasImageExtension = imageExtensions.some(ext => trimmed.toLowerCase().includes(ext));
        
        // URLs de CDN de imagens conhecidos são sempre aceitos
        const imageHosts = ['images.unsplash.com', 'picsum.photos', 'via.placeholder.com', 'imgur.com', 'i.imgur.com', 'cloudinary.com'];
        const isImageHost = imageHosts.some(host => url.hostname.includes(host));
        
        if (hasImageExtension || isImageHost) {
          return trimmed;
        } else {
          return trimmed;
        }
      } catch (e) {
        return 'https://via.placeholder.com/300x200?text=URL+Invalida';
      }
    }

    if (trimmed.startsWith('/') || trimmed.startsWith('./') || trimmed.startsWith('../')) {
      return trimmed;
    }

    return 'https://via.placeholder.com/300x200?text=Formato+Invalido';
  }

  /**
   * Handler para clique na imagem (lightbox)
   */
  onImageClick(event: MouseEvent): void {
    const props = this.node.props as any;
    if (props.enableLightbox && !this.isPreview) {
      event.preventDefault();
      event.stopPropagation();
      this.showLightbox(this.getImageUrl());
    }
  }

  // ── Dependências entre campos de pesquisa (cascading search) ──────────

  /**
   * Executa field mappings diretos para campos não-search.
   * Para campos search, os mappings são tratados em webhook-form-search.emitFieldMappings().
   */
  private executeDirectFieldMappings(value: any): void {
    if (!this.dataSyncService || !this.node?.props) return;
    if (this.node.type === 'search' || this.node.type === 'querySelect') return;

    const mappings: any[] = (this.node.props as any)?.fieldMappings;
    if (!mappings || mappings.length === 0) return;

    for (const mapping of mappings) {
      if (!mapping.targetDataPath) continue;
      this.dataSyncService.emitFieldSync(
        mapping.targetDataPath,
        value,
        'fieldMapping_' + this.node.id
      );
      this.dataSyncService.onFormFieldChange(mapping.targetDataPath, value);
    }
  }

  /**
   * Configura a lógica de dependências para campos com dependencies configuradas.
   * Escuta mudanças nos campos-pai via fieldSync$ e atualiza:
   * - condition do searchProperties (filtragem dinâmica, apenas para search/querySelect com filterColumn)
   * - isDisabled + mensagem quando pré-requisito não satisfeito
   * - clearOnChange: limpa o valor do campo quando uma dependência muda
   */
  private setupSearchDependencies(): void {
    const deps: SearchFieldDependency[] = (this.node.props as any)?.dependencies;
    if (!deps || deps.length === 0) return;

    this.hasDependencies = true;

    // ✅ CORREÇÃO: Resolver sourceDataPath obsoleto (ex: campo mudou de dataPath após configurar dependência)
    // Se o sourceDataPath não corresponde a nenhum node por dataPath, tenta encontrar por field name/ID
    if (this.builderService) {
      const allNodes = this.builderService.currentState?.nodes || [];
      for (const dep of deps) {
        const matchByDataPath = allNodes.find(n =>
          n.id !== this.node.id && (n.props as any)?.dataPath === dep.sourceDataPath
        );
        if (!matchByDataPath) {
          // sourceDataPath pode ser um field ID antigo — procurar o node e usar o dataPath atual
          const matchByFieldId = allNodes.find(n =>
            (n.id === dep.sourceDataPath || (n.props as any)?.field === dep.sourceDataPath)
            && n.id !== this.node.id
          );
          if (matchByFieldId && (matchByFieldId.props as any)?.dataPath) {
            console.log(`🔄 [Dependency] Resolvendo sourceDataPath obsoleto: "${dep.sourceDataPath}" → "${(matchByFieldId.props as any).dataPath}"`);
            dep.sourceDataPath = (matchByFieldId.props as any).dataPath;
          }
        }
      }
    }

    // Guardar a condition original (estática) do searchProperties para combinar depois
    const sp = this.webhookField?.searchProperties as any;
    this.dependencyBaseCondition = sp?.condition || '';

    // Tentar obter valores iniciais dos campos-pai a partir da seleção atual
    if (this.dataSyncService) {
      const currentItem = this.dataSyncService.getCurrentSelectedItem?.()
                       || this.dataSyncService.getMainSelectedItem?.();
      if (currentItem) {
        for (const dep of deps) {
          const val = currentItem[dep.sourceDataPath];
          if (val != null && val !== '') {
            // Se o valor é um objeto {key, value} (showKey), pegar apenas .key
            const resolved = (typeof val === 'object' && val.key != null) ? val.key : val;
            this.dependencyValues.set(dep.sourceDataPath, resolved);
          }
        }
      }
    }

    // Aplicar estado inicial
    this.updateDependencyState(deps);

    // Subscrever a mudanças nos campos-pai
    if (this.dataSyncService) {
      this.subscription.add(
        this.dataSyncService.fieldSync$.pipe(
          takeUntil(this.destroy$),
          filter(event => {
            // Só processar se for um dos campos-pai e NÃO vier do próprio node
            return event.sourceNodeId !== this.node.id
                && deps.some(d => d.sourceDataPath === event.dataPath);
          })
        ).subscribe(event => {
          const dep = deps.find(d => d.sourceDataPath === event.dataPath);
          if (!dep) return;

          const oldValue = this.dependencyValues.get(dep.sourceDataPath);

          // Resolver valor: se objeto {key, value}, pegar .key
          let newVal = event.value;
          if (newVal != null && typeof newVal === 'object' && newVal.key != null) {
            newVal = newVal.key;
          }
          // Tratar valores vazios como null
          if (newVal === '' || newVal === undefined) newVal = null;

          this.dependencyValues.set(dep.sourceDataPath, newVal);

          // Limpar valor do campo-filho quando dependência muda
          const clearOnChange = dep.clearOnChange !== false; // default true
          if (clearOnChange && oldValue !== newVal) {
            const control = this.formGroup?.get(this.webhookField?.field);
            if (control && control.value != null && control.value !== '') {
              control.setValue(null, { emitEvent: true });
            }
          }

          this.updateDependencyState(deps);
        })
      );
    }
  }

  /**
   * Recalcula o estado de dependências e atualiza o webhookField.
   * - Se algum pré-requisito falta: desabilita campo e mostra mensagem
   * - Se todos satisfeitos: habilita e aplica condition combinada
   */
  private updateDependencyState(deps: SearchFieldDependency[]): void {
    const missingDeps: SearchFieldDependency[] = [];
    const conditionParts: string[] = [];

    for (const dep of deps) {
      const val = this.dependencyValues.get(dep.sourceDataPath);
      if (val == null || val === '') {
        missingDeps.push(dep);
      } else if (dep.filterColumn) {
        const safeVal = String(val).replace(/'/g, "''");
        conditionParts.push(`${dep.filterColumn} = '${safeVal}'`);
      }
    }

    const control = this.formGroup?.get(this.webhookField?.field);
    const isSearchType = this.node.type === 'search' || this.node.type === 'querySelect';

    if (missingDeps.length > 0) {
      this.dependencyMet = false;
      this.dependencyMessage = missingDeps[0].requiredMessage
        || `Selecione ${missingDeps[0].sourceLabel} primeiro`;

      this.webhookField = {
        ...this.webhookField,
        isDisabled: true
      };

      if (control && !control.disabled) {
        control.disable({ emitEvent: false });
      }
    } else {
      this.dependencyMet = true;
      this.dependencyMessage = '';

      const originalDisabled = (this.node.props as any)?.isDisabled || false;

      if (isSearchType) {
        let finalCondition = '';
        if (this.dependencyBaseCondition) {
          finalCondition = this.dependencyBaseCondition;
        }
        const depCondition = conditionParts.join(' AND ');
        if (depCondition) {
          finalCondition = finalCondition
            ? `${finalCondition} AND ${depCondition}`
            : depCondition;
        }

        const currentSearchProps = this.webhookField.searchProperties || {} as any;
        this.webhookField = {
          ...this.webhookField,
          isDisabled: originalDisabled,
          searchProperties: {
            ...currentSearchProps,
            condition: finalCondition || undefined
          }
        };
      } else {
        this.webhookField = {
          ...this.webhookField,
          isDisabled: originalDisabled
        };
      }

      if (control && control.disabled && !originalDisabled) {
        control.enable({ emitEvent: false });
      }
    }

    this.webhookConfig = {
      fields: [{ ...this.webhookField }],
      variables: this.variables
    };

    this.cdr.markForCheck();
  }

  /**
   * Exibir imagem em lightbox
   */
  private showLightbox(imageUrl: string): void {
    // Criar modal simples com a imagem
    const modal = document.createElement('div');
    modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.9);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10000;
      cursor: pointer;
    `;
    
    const img = document.createElement('img');
    img.src = imageUrl;
    img.style.cssText = `
      max-width: 90%;
      max-height: 90%;
      object-fit: contain;
    `;
    
    modal.appendChild(img);
    document.body.appendChild(modal);
    
    // Fechar ao clicar
    modal.addEventListener('click', () => {
      document.body.removeChild(modal);
    });
  }

}
